import {
  decrementEnrichmentWizardStep,
  setDataEnrichmentId,
  setEnrichmentError,
  setEnrichmentStatus,
} from 'actions/enrichmentWizard';
import { usePost } from 'hooks';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getEnrichmentFile,
  getEnrichmentListDetails,
  getEnrichmentMappings,
  getEnrichmentSettings,
  getEnrichmentSheetName,
  getEnrichmentType,
} from 'selectors/enrichmentWizard';
import { enrichOrganisations, enrichResearchers } from 'services/api';
import { fileExtensionToMimeType } from 'utils/file';
import { format } from 'utils/date';

const usePostEnrich = () => {
  const dispatch = useDispatch();
  const mappings = useSelector(getEnrichmentMappings);
  const file = useSelector(getEnrichmentFile);
  const type = useSelector(getEnrichmentType);
  const sheetName = useSelector(getEnrichmentSheetName);
  const listDetails = useSelector(getEnrichmentListDetails);
  const settings = useSelector(getEnrichmentSettings);
  const [{ res, loading, error }, postUpload] = usePost({
    url: type === 'email' ? enrichResearchers : enrichOrganisations,
  });

  useEffect(() => {
    if (res && !loading) {
      dispatch(setEnrichmentStatus('processing'));
      dispatch(setDataEnrichmentId(res));
    }
  }, [res, loading, dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(
        setEnrichmentError(
          error?.status === 409 && error?.message === 'Enrichment.ReachedLimit'
            ? 'You have insufficient enrichments in your monthly allowance to process the rows in your file.'
            : 'Something went wrong. Please try again. If the issue persists contact your Customer Success Manager.'
        )
      );
      dispatch(setEnrichmentStatus('error'));
    }
  }, [error, dispatch]);

  const handleStartEnrichment = useCallback(() => {
    dispatch(setEnrichmentStatus('loading'));

    if (type === 'email') {
      const emailColumn = mappings.email?.column;
      if (emailColumn && mappings.email.validation?.valid?.length) {
        const researchers = mappings.email.validation.valid
          .map(x => ({
            email: x.value,
            rowNumber: x.rowNumber,
          }))
          .concat(
            mappings.email.validation.invalid.map(x => ({
              rowNumber: x.rowNumber,
              invalidReason: x.reason,
            }))
          );
        postUpload({
          researchers: researchers,
          filename: file.name,
          totalRows: mappings.email.validation.total,
          listName:
            listDetails?.listName ||
            file?.name?.split('.')?.[0] ||
            `list-${format(Date.now(), 'YYYY-MM-DD')}`,
          sheetName:
            file?.type === fileExtensionToMimeType['.xlsx'] ? sheetName : null,
          invalidRows: mappings.email.validation.invalid?.length || 0,
          validRows: mappings.email.validation.valid?.length || 0,
          emptyRows:
            mappings.email.validation.total -
            (mappings.email.validation.invalid?.length || 0) -
            (mappings.email.validation.valid?.length || 0),
          fileHasHeaders: !settings.includeHeaders,
        });
      } else {
        dispatch(decrementEnrichmentWizardStep());
        dispatch(setEnrichmentError('No mapped emails found'));
      }
    } else {
      const organisationColumn = mappings.organisation?.column;
      if (
        organisationColumn &&
        mappings.organisation.validation?.valid?.length
      ) {
        const organisations = mappings.organisation.validation.valid
          .map(x => ({
            name: x.value,
            rowNumber: x.rowNumber,
          }))
          .concat(
            mappings.organisation.validation.invalid.map(x => ({
              invalidReason: x.reason,
              rowNumber: x.rowNumber,
            }))
          );
        postUpload({
          organisations: organisations,
          filename: file.name,
          totalRows: mappings.organisation.validation.total,
          listName:
            listDetails?.listName ||
            file?.name?.split('.')?.[0] ||
            `list-${format(Date.now(), 'YYYY-MM-DD')}`,
          sheetName:
            file?.type === fileExtensionToMimeType['.xlsx'] ? sheetName : null,
          invalidRows: mappings.organisation.validation.invalid?.length || 0,
          validRows: mappings.organisation.validation.valid?.length || 0,
          emptyRows:
            mappings.organisation.validation.total -
            (mappings.organisation.validation.invalid?.length || 0) -
            (mappings.organisation.validation.valid?.length || 0),
          fileHasHeaders: !settings.includeHeaders,
        });
      } else {
        dispatch(decrementEnrichmentWizardStep());
        dispatch(setEnrichmentError('No mapped organisations found'));
      }
    }
  }, [
    mappings,
    file,
    type,
    postUpload,
    dispatch,
    listDetails,
    sheetName,
    settings,
  ]);

  return [{ res, loading, error }, handleStartEnrichment];
};

export default usePostEnrich;
